import { SiteRoute } from '@journeyapps/package-types';
import * as React from 'react';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { Documents } from '../../RoutesWidget';
import { ContentWidget } from '../../widgets/ContentWidget';
import { FAQ } from '../../widgets/FAQWidget';
import { TOCPageWidget } from '../../widgets/layout/TOCPageWidget';
import { TableOfContentsItem } from '../../widgets/TableOfContentsWidget';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

export const FAQPage: React.FC = () => {
  const faqItems = useMemo<FAQItem[]>(
    () => [
      {
        question: 'How low-code is the product? Can I write custom code?',
        answer: (
          <p>
            Yes! The product is built upon our Rapid-Code Paradigm which means that the product is designed as a
            code-first tool. Developers can make use of both low-code, no-code and pro-code technologies as they see fit
            in all JourneyApps Platform projects.
          </p>
        )
      },
      {
        question: 'Is the product full-stack? Does it ship with an editor?',
        answer: (
          <>
            <p>Yes! The product is a complete end-to-end full-stack solution for building any kind of application.</p>
            <p>
              All the tools to build and run apps are provided as a managed solution and everything is built on top of
              the most advanced web technologies while deeply integrating with all low-level APIs on all devices and
              platforms.
              <br />
              <Link to={SiteRoute.OXIDE}>Learn more about OXIDE, our web-based IDE</Link>.
            </p>
            <p>
              The platform is also designed to be open. All code and assets are backed by Git, and so developers can
              also build using their own tools, IDEs and technologies as they see fit.
              <br />
              <Link to={SiteRoute.FEATURES}>Learn more about all the features</Link>.
            </p>
          </>
        )
      },
      {
        question: 'Is the product open-source?',
        answer: (
          <>
            <p>
              The platform is closed-source, <b>but is in the process of becoming open-source.</b> During 2025,
              JourneyApps decided to take the product in a new direction and started{' '}
              <a href="https://labs.journeyapps.com">the JourneyApps Labs project</a> to start moving various components
              of the product into the open.
            </p>
            <p>
              Over the coming years, more parts of the product are going to make their way over to Labs, and an
              open-source, self-hostable version of the platform will become available.
            </p>
          </>
        )
      },
      {
        question: 'Do you offer a trial?',
        answer: (
          <p>
            Yes: you can head on over to{' '}
            <a href="https://accounts.journeyapps.com/portal/get-started">
              accounts.journeyapps.com/portal/get-started
            </a>{' '}
            to sign up and start a free trial.
          </p>
        )
      },
      {
        question: 'What kind of apps can you build with the product?',
        answer: (
          <>
            <p>
              The JourneyApps Platform allows developers to build apps for any platform and any device. While the
              product has strong roots in mobile applications, it also has first-class support for desktop and web
              applications.
            </p>
            <p>
              With desktop applications, the product provides all the integrations you would expect such as desktop
              notifications, deep-linking, file-system access and more.
            </p>
          </>
        )
      },
      {
        question: 'Can I use the product to create public-facing applications such as those deployed on an app store?',
        answer: (
          <p>
            Yes! Developers can sign apps with custom provisioning profiles and create fully custom branded applications
            with your own custom SSO baked directly into the app.
          </p>
        )
      },
      {
        question: 'How secure is the product?',
        answer: (
          <p>
            Please check out our extensive <a href={Documents.SECURITY}>white paper</a> on security.
          </p>
        )
      },
      {
        question: 'Which is better, DB and OnlineDB or the REST API for retrieving and manipulating data?',
        answer: (
          <p>
            We have written a small comparison document to answer questions surrounding using DB vs the REST API,
            whether you should call them from the app directly or from a CloudCode task, and a few best practice tips.{' '}
            <Link to={`${SiteRoute.BLOG}faq-db-vs-api-vs-cc`}>Read the comparison guide</Link>.
          </p>
        )
      }
    ],
    []
  );

  const tableOfContents = useMemo<TableOfContentsItem[]>(
    () =>
      faqItems.map((item) => ({
        depth: 2,
        text: item.question
      })),
    [faqItems]
  );

  return (
    <TOCPageWidget title={'FAQ'} width={1180} tableOfContents={tableOfContents}>
      <ContentWidget>
        <h1>FAQ</h1>
        {faqItems.map((item) => (
          <FAQ key={item.question} question={item.question}>
            {item.answer}
          </FAQ>
        ))}
      </ContentWidget>
    </TOCPageWidget>
  );
};
